import { TourTip } from '@/components/TourTip';
/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryLayout.tsx
 * Green accent. Tabs: All / Low stock / Expiring.
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { Package, Boxes, Clock, AlertTriangle } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { usePantryPulse } from '@/hooks/usePantry';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface PantryContext {
  homeId: string;
  homeName: string;
}

export function usePantryContext(): PantryContext {
  return useOutletContext<PantryContext>();
}

// Shared reference data for categories + units + locations
export const CATEGORIES = [
  { value: 'produce',    label: 'Produce',    emoji: '🥬' },
  { value: 'dairy',      label: 'Dairy',      emoji: '🥛' },
  { value: 'grains',     label: 'Grains',     emoji: '🌾' },
  { value: 'proteins',   label: 'Proteins',   emoji: '🥩' },
  { value: 'canned',     label: 'Canned',     emoji: '🥫' },
  { value: 'frozen',     label: 'Frozen',     emoji: '🧊' },
  { value: 'snacks',     label: 'Snacks',     emoji: '🍿' },
  { value: 'beverages',  label: 'Beverages',  emoji: '🥤' },
  { value: 'condiments', label: 'Condiments', emoji: '🫙' },
  { value: 'spices',     label: 'Spices',     emoji: '🧂' },
  { value: 'cleaning',   label: 'Cleaning',   emoji: '🧼' },
  { value: 'personal',   label: 'Personal',   emoji: '🧴' },
  { value: 'other',      label: 'Other',      emoji: '📦' },
];

export const LOCATIONS = [
  { value: 'pantry',  label: 'Pantry',  emoji: '🗄️' },
  { value: 'fridge',  label: 'Fridge',  emoji: '🧊' },
  { value: 'freezer', label: 'Freezer', emoji: '❄️' },
  { value: 'counter', label: 'Counter', emoji: '🍽️' },
  { value: 'cabinet', label: 'Cabinet', emoji: '🚪' },
  { value: 'other',   label: 'Other',   emoji: '📍' },
];

export const UNITS = [
  'pcs', 'kg', 'g', 'L', 'ml', 'pack', 'bottle', 'box', 'can', 'bunch', 'dozen',
];

export default function PantryLayout() {
  const { data: homesData, isLoading } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';

  const { data: pulse } = usePantryPulse(homeId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        
      <TourTip
        id="pantry-v1"
        title={"Your kitchen inventory"}
        body={"Never run out of ugali flour or cooking oil again."}
        bullets={["Add items with expiry dates — Kejja reminds you", "Low-stock list shows what to buy next", "Shared with every member of your home"]}
        accent="emerald"
      />
      <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!homeId) return <div className="text-sm text-muted-foreground">No home found.</div>;

  const lowCount = (pulse?.lowStockCount ?? 0) + (pulse?.outOfStockCount ?? 0);
  const expiringCount = (pulse?.expiringSoonCount ?? 0) + (pulse?.expiredCount ?? 0);

  const ctx: PantryContext = { homeId, homeName };

  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold leading-tight tracking-tight">Pantry</h1>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              <span className="italic">What's in the house.</span>
              {homeName && <span className="ml-2">· {homeName}</span>}
            </p>
          </div>
        </div>
      </header>

      <nav className="flex items-center gap-1 overflow-x-auto border-b border-border">
        <TabLink to="/pantry" end icon={Boxes} label="All items" count={pulse?.totalItems} />
        <TabLink to="/pantry/low-stock" icon={AlertTriangle} label="Low stock" count={lowCount} urgent={lowCount > 0} />
        <TabLink to="/pantry/expiring" icon={Clock} label="Expiring" count={expiringCount} urgent={expiringCount > 0} />
      </nav>

      <Outlet context={ctx} />
    </div>
  );
}

function TabLink({
  to, end, icon: Icon, label, count, urgent,
}: {
  to: string; end?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string; count?: number; urgent?: boolean;
}) {
  return (
    <NavLink
      to={to} end={end}
      className={({ isActive }) => cn(
        'flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
        isActive
          ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {typeof count === 'number' && count > 0 && (
        <span className={cn(
          'text-[10px] tabular-nums px-1.5 py-0.5 rounded-full',
          urgent ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300' : 'bg-muted',
        )}>{count}</span>
      )}
    </NavLink>
  );
}
